// src/components/templates/ItemCard.jsx
import React, { useState } from 'react';
import { 
  MessageCircle, Phone, Mail, Eye, Heart, Share2, 
  Star, MapPin, Clock, Tag, DollarSign, Edit2, Copy, Trash2,
  Car, Home, ShoppingCart, Utensils, Wrench
} from 'lucide-react';
import { catalogService } from '../../services/catalogService';

export const UniversalItemCard = ({ item, catalog, viewMode = 'grid', onItemClick, onContact,
  showEditControls = false, onEdit, onDelete, onDuplicate  }) => {
  const [liked, setLiked] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Get appropriate icon based on category
  const getCategoryIcon = (category) => {
    const icons = {
      'automotive': Car,
      'real-estate': Home,
      'retail': ShoppingCart,
      'food': Utensils,
      'services': Wrench,
      'default': Tag
    };
    const IconComponent = icons[category] || icons.default;
    return <IconComponent className="h-4 w-4" />;
  };

  const handleContact = async (method, contactInfo) => {
    // Track the contact
    await catalogService.trackContact(catalog.id, item.id, method);

    switch (method) {
      case 'whatsapp':
        const message = `Hi! I'm interested in ${item.name}${item.price ? ` - ${catalogService.formatPrice(item.price)}` : ''}. Please provide more details.`;
        const whatsappUrl = `https://wa.me/${contactInfo}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        break;
      case 'phone':
        window.location.href = `tel:${contactInfo}`;
        break;
      case 'email':
        const subject = `Inquiry about ${item.name}`;
        const body = `Hi,\n\nI'm interested in ${item.name}${item.price ? ` priced at ${catalogService.formatPrice(item.price)}` : ''}.\n\nPlease provide more details.\n\nThank you!`;
        window.location.href = `mailto:${contactInfo}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        break;
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: item.name,
      text: `Check out ${item.name}${item.price ? ` - ${catalogService.formatPrice(item.price)}` : ''}`,
      url: window.location.href
    };

    if (navigator.share) {
      await navigator.share(shareData);
    } else {
      navigator.clipboard.writeText(`${shareData.title} - ${shareData.url}`);
      alert('Link copied to clipboard!');
    }
  };

  const handleView = async () => {
    await catalogService.trackView(catalog.id, item.id);
  };

  if (viewMode === 'list') {
    return <ItemListView 
      item={item} 
      catalog={catalog}
      onContact={handleContact}
      onShare={handleShare}
      onView={handleView}
      getCategoryIcon={getCategoryIcon}
    />;
  }

  return (
    <div 
      className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden group relative ${
        viewMode === 'list' ? 'flex' : ''
      }`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => !showEditControls && onItemClick?.(item)}
    >
      {/* Quick Edit Controls Overlay - only show in ItemManager */}
      {showEditControls && (
        <div className={`absolute top-2 right-2 z-10 flex gap-1 transition-all duration-200 ${
          showActions ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
        }`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            className="p-2 bg-white/90 hover:bg-blue-50 text-blue-600 rounded-lg shadow-sm border backdrop-blur-sm"
            title="Edit item"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate?.();
            }}
            className="p-2 bg-white/90 hover:bg-green-50 text-green-600 rounded-lg shadow-sm border backdrop-blur-sm"
            title="Duplicate item"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this item?')) {
                onDelete?.();
              }
            }}
            className="p-2 bg-white/90 hover:bg-red-50 text-red-600 rounded-lg shadow-sm border backdrop-blur-sm"
            title="Delete item"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Image Section */}
      <div className="relative h-64 bg-gray-200">
        {!imageError && item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            {getCategoryIcon(item.category)}
          </div>
        )}

        {/* Overlay Actions */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLiked(!liked);
            }}
            className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
              liked 
                ? 'bg-red-500 text-white' 
                : 'bg-white/80 text-gray-700 hover:bg-white'
            }`}
          >
            <Heart className="h-4 w-4" fill={liked ? 'currentColor' : 'none'} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShare();
            }}
            className="p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white backdrop-blur-sm transition-colors"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>

        {/* Primary Contact Button */}
        {catalog.whatsappNumber && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleContact('whatsapp', catalog.whatsappNumber);
            }}
            className="absolute top-4 left-4 bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition-colors shadow-lg"
          >
            <MessageCircle className="h-5 w-5" />
          </button>
        )}

        {/* Featured/Status Badge */}
        {item.featured && (
          <div className="absolute bottom-4 left-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <Star className="h-3 w-3" fill="currentColor" />
            FEATURED
          </div>
        )}

        {item.status === 'sold' && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <span className="bg-red-600 text-white px-6 py-2 rounded-full font-bold text-lg">
              SOLD
            </span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Title and Category */}
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            {item.name}
          </h3>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            {getCategoryIcon(item.category)}
            <span className="capitalize">{item.category}</span>
            {item.location && (
              <>
                <span>•</span>
                <MapPin className="h-4 w-4" />
                <span>{item.location}</span>
              </>
            )}
          </div>
        </div>

        {/* Key Features/Specs */}
        {item.specs && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {Object.entries(item.specs).slice(0, 4).map(([key, value]) => (
              <div key={key} className="text-sm text-gray-600">
                <span className="font-medium capitalize">{key}:</span> {value}
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        {item.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {item.description}
          </p>
        )}

        {/* Pricing and Actions */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              {item.price ? (
                <>
                  <p className="text-2xl font-bold text-gray-900">
                    {catalogService.formatPrice(item.price)}
                  </p>
                  {item.category === 'automotive' && (
                    <p className="text-sm text-gray-600">
                      Est. {catalogService.formatPrice(
                        catalogService.calculateMonthlyPayment(item.price)
                      ).replace('R ', '')} p/m
                    </p>
                  )}
                </>
              ) : (
                <p className="text-lg font-semibold text-gray-900">
                  Contact for Price
                </p>
              )}
            </div>
            
            <div className="text-right">
              <p className="font-semibold text-gray-900">
                {catalog.companyName || catalog.botName}
              </p>
              {item.rating && (
                <div className="flex items-center gap-1 text-sm text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className="h-3 w-3" 
                      fill={i < item.rating ? 'currentColor' : 'none'} 
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact Buttons */}
          <div className="flex gap-2">
            {catalog.whatsappNumber && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleContact('whatsapp', catalog.whatsappNumber);
                }}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </button>
            )}
            
            {catalog.contactNumber && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleContact('phone', catalog.contactNumber);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="h-4 w-4" />
                Call
              </button>
            )}

            {catalog.email && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleContact('email', catalog.email);
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Email
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// List view component
const ItemListView = ({ item, catalog, onContact, onShare, onView, getCategoryIcon }) => (
  <div 
    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
    onClick={onView}
  >
    <div className="flex">
      {/* Image */}
      <div className="w-64 h-48 bg-gray-200 flex-shrink-0">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {getCategoryIcon(item.category)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-6 flex justify-between">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {item.name}
          </h3>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            {getCategoryIcon(item.category)}
            <span className="capitalize">{item.category}</span>
            {item.location && (
              <>
                <span>•</span>
                <MapPin className="h-4 w-4" />
                <span>{item.location}</span>
              </>
            )}
          </div>

          {item.description && (
            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
              {item.description}
            </p>
          )}

          {/* Key specs in list view */}
          {item.specs && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              {Object.entries(item.specs).slice(0, 4).map(([key, value]) => (
                <span key={key}>
                  <span className="font-medium capitalize">{key}:</span> {value}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Price and Actions */}
        <div className="text-right ml-6">
          {item.price ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {catalogService.formatPrice(item.price)}
              </p>
              {item.category === 'automotive' && (
                <p className="text-sm text-gray-600 mb-4">
                  Est. {catalogService.formatPrice(
                    catalogService.calculateMonthlyPayment(item.price)
                  ).replace('R ', '')} p/m
                </p>
              )}
            </>
          ) : (
            <p className="text-lg font-semibold text-gray-900 mb-4">
              Contact for Price
            </p>
          )}

          <div className="flex flex-col gap-2">
            {catalog.whatsappNumber && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContact('whatsapp', catalog.whatsappNumber);
                }}
                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </button>
            )}
            
            {catalog.contactNumber && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContact('phone', catalog.contactNumber);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="h-4 w-4" />
                Call
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);